import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

const ToastContext = createContext(null);

let idCounter = 0;

/* ── Imperativt API (A9) ──
   kpToast.error('Kunde inte spara') fungerar från vilken fil som helst — även
   vanliga JS-moduler utan hook-åtkomst (api-lager, hooks, hjälpfunktioner).
   Providern registrerar sin push-funktion här när den monteras. Saknas provider
   (t.ex. isolerat Jest-test) loggas meddelandet till konsolen i stället. */
let imperativePush = null;
const TTL = { success: 4000, info: 4000, warning: 6000, error: 7000 };

function visa(type, message, opts) {
  const text = message == null ? '' : String(message);
  if (!imperativePush) {
    if (typeof console !== 'undefined') console.warn(`[kpToast.${type} utan ToastProvider] ${text}`);
    return null;
  }
  return imperativePush({ type, message: text, ttl: (opts && opts.ttl) || TTL[type] || 4000 });
}

export const kpToast = {
  success: (message, opts) => visa('success', message, opts),
  error:   (message, opts) => visa('error', message, opts),
  warning: (message, opts) => visa('warning', message, opts),
  info:    (message, opts) => visa('info', message, opts),
};

/* ── Inline styles (no Tailwind dependency) ──
   top: 68 = 52px HeaderBar height + 16px gap. Portal to document.body
   avoids stacking-context traps from layout wrappers (sticky header
   would otherwise overlap toasts even at high z-index).               */
const containerStyle = {
  position: 'fixed',
  zIndex: 9999,
  top: 68,
  right: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  maxWidth: 'min(420px, calc(100vw - 32px))', // fältappen (430px): toasten får inte sticka utanför
  pointerEvents: 'none',
};

const toastBase = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 16px',
  background: 'var(--kp-white, #fff)',
  border: '1px solid var(--kp-border, #E2E8F0)',
  borderRadius: 10,
  boxShadow: '0 4px 20px rgba(30, 41, 59, 0.10)',
  pointerEvents: 'auto',
  animation: 'kp-toast-in 300ms cubic-bezier(.16, 1, .3, 1) forwards',
};

const iconCircle = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const variants = {
  success: { bg: 'var(--kp-green-light, #ECFDF5)', color: 'var(--kp-green-ink, #059669)' },
  error:   { bg: 'var(--kp-red-light, #FEF2F2)',   color: 'var(--kp-red-ink, #DC2626)' },
  warning: { bg: 'var(--kp-amber-light, #FFFBEB)', color: 'var(--kp-amber-ink, #D97706)' },
  info:    { bg: 'var(--kp-accent-light, #EFF6FF)', color: 'var(--kp-accent-ink, #3B82F6)' },
};

const iconPaths = {
  success: <path d="M11.5 3.5L5.5 10 2.5 7" />,
  error:   <><circle cx="7" cy="7" r="5.5" /><path d="M7 4.5v3" /><circle cx="7" cy="9.5" r=".5" fill="currentColor" /></>,
  warning: <><path d="M7 1.5L1 12h12L7 1.5z" /><path d="M7 6v2.5" /><circle cx="7" cy="10.25" r=".5" fill="currentColor" /></>,
  info:    <><circle cx="7" cy="7" r="5.5" /><path d="M7 6.5V10" /><circle cx="7" cy="4.5" r=".5" fill="currentColor" /></>,
};

const msgStyle = {
  flex: 1,
  fontSize: '13.5px',
  fontWeight: 500,
  color: 'var(--kp-text, #1E293B)',
  lineHeight: 1.4,
};

const closeBtnStyle = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  color: 'var(--kp-text-faint, #8D9DB0)',
  flexShrink: 0,
  transition: 'all 150ms',
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timeouts = useRef({});

  const remove = useCallback((id) => {
    setToasts(ts => ts.filter(t => t.id !== id));
    if(timeouts.current[id]) {
      clearTimeout(timeouts.current[id]);
      delete timeouts.current[id];
    }
  }, []);

  const push = useCallback((toast) => {
    const id = ++idCounter;
    const payload = { id, type: toast.type || 'info', message: toast.message, ttl: toast.ttl || 4000 };
    setToasts(ts => [...ts, payload]);
    timeouts.current[id] = setTimeout(() => remove(id), payload.ttl);
    return id;
  }, [remove]);

  // clear timeouts on unmount
  useEffect(() => () => { Object.values(timeouts.current).forEach(clearTimeout); }, []);

  // Inject animation keyframes once
  useEffect(() => {
    if (!document.getElementById('kp-toast-keyframes')) {
      const s = document.createElement('style');
      s.id = 'kp-toast-keyframes';
      s.textContent = '@keyframes kp-toast-in{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}';
      document.head.appendChild(s);
    }
  }, []);

  // Registrera det imperativa API:t så länge providern lever.
  useEffect(() => {
    imperativePush = push;
    return () => { if (imperativePush === push) imperativePush = null; };
  }, [push]);

  /* Samma värde mellan renderingar. Ett nytt objekt per rendering gjorde att
     varje visad eller borttagen ruta ändrade useToast() hos alla konsumenter -
     en laddfunktion med toast som beroende laddade då om, och vid fel blev det
     en evig loop (Utlägg 2026-10-16, a3863c3cc). */
  const ctx = useMemo(() => ({ push, remove }), [push, remove]);

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div aria-live="polite" aria-atomic="true" style={containerStyle}>
          {toasts.map(t => <Toast key={t.id} toast={t} onDismiss={() => remove(t.id)} />)}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast(){
  const ctx = useContext(ToastContext);
  if(!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

function Toast({ toast, onDismiss }) {
  const v = variants[toast.type] || variants.info;
  return (
    <div style={toastBase} role="status">
      <div style={{ ...iconCircle, background: v.bg, color: v.color }}>
        <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
          {iconPaths[toast.type] || iconPaths.info}
        </svg>
      </div>
      <span style={msgStyle}>{toast.message}</span>
      <button onClick={onDismiss} style={closeBtnStyle} aria-label="Stäng" title="Stäng">
        <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 14, height: 14 }}>
          <path d="M3 3l8 8" /><path d="M11 3l-8 8" />
        </svg>
      </button>
    </div>
  );
}
